import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CityAutocomplete } from "../../components/CityAutocomplete.js";
import type { City } from "../../lib/api/cities.js";
import { type ProfileUser, profileApi } from "../../lib/api/profile.js";
import { useAuth } from "../../lib/auth-context.js";
import "./Profile.css";

function ProfileEditor({ userId }: { userId: string }) {
  const [user, setUser] = useState<ProfileUser | null>(null);
  const [interests, setInterests] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    profileApi.get(userId).then((r) => {
      if (r.success && r.data) {
        setUser(r.data.user);
        setInterests(r.data.interests.join(", "));
      }
    });
  }, [userId]);

  if (!user) return <p className="prf__muted">Loading…</p>;

  function set<K extends keyof ProfileUser>(key: K, value: ProfileUser[K]) {
    setUser((u) => (u ? { ...u, [key]: value } : u));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    await profileApi.update(userId, {
      name: user.name,
      bio: user.bio ?? "",
      status: user.status ?? "",
      city: user.city ?? "",
      country: user.country ?? "",
      country_code: user.country_code ?? "",
      timezone: user.timezone ?? "",
      ...(user.latitude != null ? { latitude: user.latitude } : {}),
      ...(user.longitude != null ? { longitude: user.longitude } : {}),
      working_start: user.working_start ?? "",
      working_end: user.working_end ?? "",
    });
    await profileApi.setInterests(
      userId,
      interests
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    );
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  return (
    <form className="prf__card" onSubmit={save}>
      <h2 className="prf__h2">You</h2>
      <label className="prf__field">
        <span>Name</span>
        <input value={user.name} onChange={(e) => set("name", e.target.value)} />
      </label>
      <label className="prf__field">
        <span>Status</span>
        <input
          value={user.status ?? ""}
          placeholder="What you're up to"
          onChange={(e) => set("status", e.target.value)}
        />
      </label>
      <label className="prf__field">
        <span>Bio</span>
        <textarea value={user.bio ?? ""} rows={3} onChange={(e) => set("bio", e.target.value)} />
      </label>
      <div className="prf__field">
        <span>Home city</span>
        <CityAutocomplete
          label="home city"
          initial={user.city ? `${user.city}, ${user.country ?? ""}` : ""}
          onSelect={(city: City) => {
            set("city", city.name);
            set("country", city.country);
            set("country_code", city.country_code);
            set("timezone", city.timezone);
            set("latitude", city.latitude);
            set("longitude", city.longitude);
          }}
        />
      </div>
      <div className="prf__row">
        <label className="prf__field">
          <span>Work starts</span>
          <input
            type="time"
            value={user.working_start ?? ""}
            onChange={(e) => set("working_start", e.target.value)}
          />
        </label>
        <label className="prf__field">
          <span>Work ends</span>
          <input
            type="time"
            value={user.working_end ?? ""}
            onChange={(e) => set("working_end", e.target.value)}
          />
        </label>
      </div>
      <label className="prf__field">
        <span>Interests (comma-separated)</span>
        <input
          value={interests}
          placeholder="surf, coffee, climbing"
          onChange={(e) => setInterests(e.target.value)}
        />
      </label>
      <button type="submit" className="btn btn-primary">
        {saved ? "Saved ✓" : "Save profile"}
      </button>
    </form>
  );
}

export function Profile() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <section className="prf">
      <h1 className="prf__h1">Profile</h1>
      <ProfileEditor userId={user.id} />
      <Link className="prf__sharing" to="/app/trips">
        Trips — where you're headed →
      </Link>
      <Link className="prf__sharing" to="/app/sharing">
        Sharing &amp; circles — who sees what →
      </Link>
      <Link className="prf__sharing" to="/app/groups">
        Groups — shareable trip schedules →
      </Link>
      <Link className="prf__sharing" to="/app/orgs">
        Organizations — teams &amp; coworker visibility →
      </Link>
      <Link className="prf__sharing" to="/app/tax">
        Days by country — tax residency →
      </Link>
      <Link className="prf__sharing" to="/app/integrations">
        Export &amp; integrations — calendar feed, Notion →
      </Link>
    </section>
  );
}
